import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { BOARD_SCROLLER_PROPS } from "./boardAttributes";
import { BoardScrollEdges } from "./BoardScrollEdges";
import { BoardScrollerContext } from "./useBoardWindow";
import { useScrollingAttribute } from "./useScrollingAttribute";
import styles from "./Board.module.scss";

interface BoardScrollColumnProps {
    labelledBy: string;
    style: CSSProperties;
    header: ReactNode;
    children: ReactNode;
}

/** A column of a board without lanes: its cards scroll under its header. */
export const BoardScrollColumn = ({
    labelledBy,
    style,
    header,
    children,
}: Readonly<BoardScrollColumnProps>) => {
    const [scroller, setScroller] = useState<HTMLElement | null>(null);
    useScrollingAttribute(scroller);
    const scrollerContext = useMemo(
        () => ({ element: scroller, isShared: false }),
        [scroller],
    );
    return (
        <section
            aria-labelledby={labelledBy}
            className={styles.column}
            style={style}
        >
            {header}
            <div
                ref={setScroller}
                className={styles.columnScroller}
                {...BOARD_SCROLLER_PROPS}
            >
                <BoardScrollerContext.Provider value={scrollerContext}>
                    {children}
                </BoardScrollerContext.Provider>
            </div>
            <BoardScrollEdges
                scroller={scroller}
                axis="y"
                look="fade"
                className={styles.columnEdges}
            />
        </section>
    );
};
